import { DeletionForm, NewBranchForm } from "@/schema/schema";
import { Branch, BranchMember, BranchMemberId, ChangeActiveBranch } from "@/types/branch";
import { api } from "@/utils/axiosInterceptor";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useBranch = (
  branchId: string,
  searchBranch?: string,
  searchTerm?: string
) => {
  const {
    data: branches,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["branches"],
    queryFn: async () => {
      const response = await api.get<Branch[]>("/branches");

      return response.data;
    },
    select: (data) =>
      data.filter((branch) =>
        branch.prefixCode
          .toLowerCase()
          .includes(searchBranch?.toLowerCase() ?? "")
      ),
    retry: 1,
  });

  const { data: branchDetails, isLoading: isLoadingBranch } = useQuery({
    queryKey: ["branchDetails", branchId],
    queryFn: async () => {
      const response = await api.get<NewBranchForm>(`/branch/${branchId}`);

      return response.data;
    },
    enabled: !["", "CreateBranch"].includes(branchId),
  });

  const addBranchMutation = useMutation({
    mutationFn: (data: NewBranchForm) => api.post("/branch", data),
  });

  const updateBranchMutation = useMutation({
    mutationFn: (data: NewBranchForm) => api.put(`/branch/${branchId}`, data),
  });

  const removeBranchMutation = useMutation({
    mutationFn: (data: DeletionForm) =>
      api.delete(`/branch/${branchId}`, { data }),
  });

  const { data: branchMembers, isLoading: loadingMembers } = useQuery({
    queryKey: ["branchMembers", branchId],
    queryFn: async () => {
      const response = await api.get<BranchMember[]>(
        `/branch/members/${branchId}`
      );

      return response.data;
    },
    select: (data) =>
      data.filter((branch) =>
        branch.email
          .toLowerCase()
          .includes(searchTerm?.toLowerCase() ?? "")
      ),
    enabled: !["", "CreateBranch"].includes(branchId),
    retry: false,
  });

  const addMemberMutation = useMutation({
    mutationFn: (data: BranchMemberId) =>
      api.post(`/branch/members/${branchId}`, data),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (data: BranchMemberId) =>
      api.delete(`/branch/members/${branchId}`, { data }),
  });

  const changeActiveBranchMutation = useMutation({
    mutationFn: async (data: ChangeActiveBranch) =>
      await api.post ("/active-branch", data)
  })

  const { data: businessMembers, isLoading: isLoadingBusinessMembers } = useQuery({
    queryKey: ["businessMembers", branchId],
    queryFn: async () => {
      const response = await api.get<BranchMember[]>(`/business/members/${branchId}`);

      return response.data;
    },
    select: (data) =>
      data.filter((branch) =>
        branch.email
          .toLowerCase()
          .includes(searchTerm?.toLowerCase() ?? "")
      ),
    enabled: !["", "CreateBranch"].includes(branchId),
  });

  return {
    branches: branches ?? [],
    isLoading,
    error,
    branchMembers: branchMembers ?? [],
    loadingMembers,
    addMember: addMemberMutation.mutateAsync,
    removeMember: removeMemberMutation.mutateAsync,
    addBranch: addBranchMutation.mutateAsync,
    updateBranch: updateBranchMutation.mutateAsync,
    isCreating: addBranchMutation.isPending || updateBranchMutation.isPending,
    branchDetails,
    isLoadingBranch,
    removeBranch: removeBranchMutation.mutateAsync,
    isRemoving:
      removeBranchMutation.isPending || removeBranchMutation.isPending,
    changeBranch: changeActiveBranchMutation.mutateAsync,
    isLoadingBusinessMembers,
    businessMembers,
  };
};
